import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { AdminNav } from "../components/AdminNav";
import { exportCombinedPdf } from "../admin/pdfExport";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const slug = (s) =>
  (s || "").toLowerCase().trim().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "").slice(0, 60) || "document";

const formatDate = (iso) => {
  if (!iso) return "\u2014";
  try {
    return new Date(iso).toLocaleString("en-GB", {
      day: "numeric", month: "long", year: "numeric", hour: "2-digit", minute: "2-digit",
    });
  } catch {
    return iso;
  }
};

const statusMeta = (s) => {
  if (s === "signed") return { label: "Signed", color: "#3E6E3E" };
  if (s === "viewed") return { label: "Viewed", color: "#B07A1E" };
  return { label: "Sent", color: "#7A2E2E" };
};

// Signature block injected into the signed document before capture.
const signatureBlockHtml = (detail) => {
  const date = formatDate(detail.signed_at);
  return `<div style="margin-top:40px;padding-top:22px;border-top:1px solid #7A2E2E;">
    <div style="font-variant:small-caps;letter-spacing:0.24em;font-size:11px;color:#7A2E2E;margin-bottom:14px;">Signed electronically by the client</div>
    <img src="${detail.signature_data_url}" alt="Client signature" style="display:block;max-height:120px;max-width:340px;margin-bottom:6px;" />
    <div style="border-top:0.5px solid #1C1A17;width:340px;padding-top:6px;font-size:14px;">${detail.signer_name || ""}</div>
    <div style="font-size:13px;color:#6E6A5E;margin-top:8px;">Full name: <span style="color:#1C1A17;">${detail.signer_name || ""}</span></div>
    <div style="font-size:13px;color:#6E6A5E;">Date: <span style="color:#1C1A17;">${date}</span></div>
    <div style="font-size:13px;color:#6E6A5E;">Confirmed: <span style="color:#1C1A17;">${detail.agreed ? "I have read and agree to be bound by the terms of this document." : "\u2014"}</span></div>
  </div>`;
};

const AdminSignatures = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [detail, setDetail] = useState(null);
  const [pdfBusy, setPdfBusy] = useState(false);
  const signedIframeRef = useRef(null);

  const token = typeof window !== "undefined" ? localStorage.getItem("rq_admin_token") : null;

  const load = () => {
    axios
      .get(`${API}/admin/signing-requests`, { headers: { Authorization: `Bearer ${token}` } })
      .then(({ data }) => setRows(data))
      .catch(() => {
        localStorage.removeItem("rq_admin_token");
        navigate("/admin");
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    if (!token) {
      navigate("/admin");
      return;
    }
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate, token]);

  const openDetail = async (id) => {
    setDetail({ loading: true });
    try {
      const { data } = await axios.get(`${API}/admin/signing-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setDetail({ loading: false, data });
    } catch {
      setDetail({ loading: false, error: "Could not load this document." });
    }
  };

  const removeRow = async (id) => {
    try {
      await axios.delete(`${API}/admin/signing-requests/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRows((r) => r.filter((x) => x.id !== id));
      if (detail?.data?.id === id) setDetail(null);
    } catch {
      /* no-op */
    }
  };

  const downloadSignedPdf = async () => {
    if (!signedIframeRef.current || pdfBusy) return;
    setPdfBusy(true);
    try {
      const d = detail.data;
      const filename = `${slug(d.title)}-signed-${slug(d.signer_name || d.client_email)}.pdf`;
      await exportCombinedPdf({ iframe: signedIframeRef.current, filename });
    } finally {
      setPdfBusy(false);
    }
  };

  const injectSignature = (e) => {
    try {
      const doc = e.target.contentDocument;
      if (!doc || !doc.body) return;
      const sheet = doc.querySelector(".sheet") || doc.body;
      if (!sheet.querySelector("[data-signature-block]")) {
        const wrap = doc.createElement("div");
        wrap.setAttribute("data-signature-block", "1");
        wrap.innerHTML = signatureBlockHtml(detail.data);
        sheet.appendChild(wrap);
      }
      e.target.style.height = doc.body.scrollHeight + 40 + "px";
    } catch (_) {}
  };

  return (
    <div className="min-h-screen bg-[#F4F1EA] pb-24" data-testid="admin-signatures-page">
      <AdminNav active="signatures" right={
        <button onClick={load} className="text-[11px] uppercase tracking-[0.28em] text-[#1C1A17] hover:text-[#7A2E2E] transition-colors" data-testid="signatures-refresh">
          Refresh
        </button>
      } />

      <div className="mx-auto max-w-[900px] px-6 md:px-8 pt-12">
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#7A2E2E] mb-2">The Archive</p>
        <h1 className="text-3xl sm:text-4xl italic mb-3">Client Signatures</h1>
        <p className="text-[14px] text-[#6E6A5E] max-w-[64ch] mb-10">
          Documents sent to clients for electronic signature. Once a client signs, the status turns to Signed and
          you can view and download the final signed copy here.
        </p>

        {loading ? (
          <p className="italic text-[#6E6A5E]" data-testid="signatures-loading">Loading&hellip;</p>
        ) : rows.length === 0 ? (
          <p className="italic text-[#6E6A5E]" data-testid="signatures-empty">
            No documents have been sent for signature yet. Generate a Service Agreement or Biography Proposal under
            Documents, then use &ldquo;Send for e-signature&rdquo;.
          </p>
        ) : (
          <div className="border border-[#A8A296] bg-white/60" data-testid="signatures-list">
            {rows.map((r) => {
              const m = statusMeta(r.status);
              return (
                <div key={r.id} className="flex flex-wrap items-center gap-4 px-5 py-4 border-b border-[#A8A296]/40" data-testid={`signature-row-${r.id}`}>
                  <span className="text-[10px] uppercase tracking-[0.22em] px-2 py-1 border" style={{ color: m.color, borderColor: m.color }} data-testid={`signature-status-${r.id}`}>
                    {m.label}
                  </span>
                  <div className="flex-1 min-w-[200px]">
                    <div className="text-[15px]">{r.title}</div>
                    <div className="text-[13px] text-[#6E6A5E]">
                      {r.client_name ? `${r.client_name} \u00b7 ` : ""}{r.client_email}
                    </div>
                  </div>
                  <div className="text-[12px] text-[#6E6A5E] text-right min-w-[160px]">
                    <div>Sent: {formatDate(r.created_at)}</div>
                    {r.status === "signed" && <div>Signed: {formatDate(r.signed_at)}</div>}
                  </div>
                  {r.status === "signed" && (
                    <button onClick={() => openDetail(r.id)} className="text-[11px] uppercase tracking-[0.24em] text-[#7A2E2E] hover:underline" data-testid={`signature-view-${r.id}`}>
                      View
                    </button>
                  )}
                  <button onClick={() => removeRow(r.id)} className="text-[11px] uppercase tracking-[0.24em] text-[#A8A296] hover:text-[#7A2E2E]" data-testid={`signature-delete-${r.id}`}>
                    Delete
                  </button>
                </div>
              );
            })}
          </div>
        )}

        {detail && (
          <div className="mt-12" data-testid="signature-detail">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-[12px] uppercase tracking-[0.28em] text-[#7A2E2E]">Signed document</h2>
              <button onClick={() => setDetail(null)} className="text-[12px] italic text-[#6E6A5E] hover:text-[#7A2E2E]" data-testid="signature-detail-close">Close</button>
            </div>
            {detail.loading ? (
              <p className="italic text-[#6E6A5E]">Loading&hellip;</p>
            ) : detail.error ? (
              <p className="text-[#7A2E2E]">{detail.error}</p>
            ) : (
              <>
                <div className="flex items-center gap-4 mb-4">
                  <button onClick={downloadSignedPdf} disabled={pdfBusy} className="bg-[#1C1A17] text-[#F4F1EA] px-6 py-3 text-[12px] uppercase tracking-[0.22em] hover:bg-[#7A2E2E] transition-colors disabled:opacity-60" data-testid="signature-download-pdf">
                    {pdfBusy ? "Preparing PDF\u2026" : "Download signed PDF"}
                  </button>
                  <span className="text-[13px] text-[#6E6A5E]">
                    Signed by {detail.data.signer_name} on {formatDate(detail.data.signed_at)}
                  </span>
                </div>
                <div className="border border-[#A8A296] bg-white">
                  <iframe
                    ref={signedIframeRef}
                    title="Signed document"
                    srcDoc={detail.data.document_html}
                    className="w-full block"
                    style={{ height: "1200px", border: "0" }}
                    onLoad={injectSignature}
                  />
                </div>
              </>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSignatures;
